import {
	Checkbox,
	ComboBox,
	DeprecatedCreativeModal as CreativeModal,
	DeprecatedScrollbar as Scrollbar,
	FlexColumn,
	FlexRow,
	Label,
	SearchField,
	SubHeading,
	Text,
} from '@forecast-it/design-system';
import React, {useEffect, useState, useMemo} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import styled from 'styled-components/macro';
import {useIntl} from 'react-intl';
import {SageIntacctImportTeamMembersModal_company} from './__generated__/SageIntacctImportTeamMembersModal_company.graphql';
import {CheckedState} from '@radix-ui/react-checkbox';
import DirectApi from '../../../directApi';
import InlineLoader from '../../../forecast-app/shared/components/inline-loader/inline_loader';
import ImportSageIntacctTeamMembersMutation from '../../../mutations/ImportSageIntacctTeamMembersMutation';
import {hideLoader, showLoader} from '../../global_loader';
import Util from '../../../forecast-app/shared/util/util';
import {getSelectableProfiles} from '../../../forecast-app/shared/util/PermissionsUtil';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {HorizontalBar} from './SageIntacctModal.styled';

type ListItemType = {
	hasBorderBottom?: boolean;
};

const scrollSectionHeight = window?.innerHeight < 900 ? '140px' : '500px';
const ListItem = styled.div<ListItemType>`
	width: 550px;
	padding: 18px 8px 18px 8px;
	border-bottom: ${({hasBorderBottom}) => (hasBorderBottom ? '1px solid #ccc' : 'none')};
`;

const ListWrapper = styled.div`
	height: ${scrollSectionHeight};
`;

type SageIntacctImportTeamMembersModalProps = {
	closeModal: () => void;
	company: SageIntacctImportTeamMembersModal_company;
};

type SageIntacctTeamMember = {
	EMPLOYEEID: string;
	DEPARTMENTID: string;
	TITLE: string;
	STARTDATE: string;
	PERSONALINFO__2e__INITIAL: string | null;
	PERSONALINFO__2e__LASTNAME: string;
	PERSONALINFO__2e__EMAIL1: string;
	PERSONALINFO__2e__FIRSTNAME: string;
	NAME?: string;
};

type ForecastProfile = {
	id: string;
	name: string | null;
};

const SageIntacctImportTeamMembersModal = ({closeModal, company}: SageIntacctImportTeamMembersModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [teamMembers, setTeamMembers] = useState<SageIntacctTeamMember[]>([]);
	const [filteredTeamMembers, setFilteredTeamMembers] = useState<SageIntacctTeamMember[]>([]);
	const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);

	const selectAll = useMemo<boolean>(() => {
		if (filteredTeamMembers.length === 0 || selectedTeamMembers.length === 0) return false;

		return filteredTeamMembers.every(tm => selectedTeamMembers.includes(tm.EMPLOYEEID));
	}, [filteredTeamMembers, selectedTeamMembers]);

	const [profiles, setProfiles] = useState<ForecastProfile[]>([]);
	const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
	const [fetchingEmployees, setFetchingEmployees] = useState(true);
	const [failed, setFailed] = useState(false);
	const [importing, setImporting] = useState(false);
	const [assignDepartment, setAssignDepartment] = useState<CheckedState>(false);
	const disableImportButton = importing || selectedTeamMembers.length === 0 || !selectedProfileId;

	useEffect(() => {
		if (company?.profiles?.edges && Array.isArray(company.profiles.edges)) {
			const sortedProfiles = getSelectableProfiles(company.profiles.edges);
			setProfiles(sortedProfiles);
		}
	}, []);

	useEffect(() => {
		DirectApi.Fetch('sage_intacct/employees').then(res => {
			if (res?.employees) {
				const tempTms = res.employees.map(t => {
					return {
						...t,
						NAME: `${t.PERSONALINFO__2e__FIRSTNAME} ${t.PERSONALINFO__2e__LASTNAME} ${
							t.PERSONALINFO__2e__EMAIL1 ? `(${t.PERSONALINFO__2e__EMAIL1})` : ''
						}`,
					};
				});
				setTeamMembers(tempTms);
				setFilteredTeamMembers(tempTms);
				setFetchingEmployees(false);
			} else if (res?.error || res?.status === 500 || res?.statusCode === 500) {
				setFailed(true);
				setFetchingEmployees(false);
			} else {
				setTeamMembers([]);
				setFilteredTeamMembers([]);
				setFetchingEmployees(false);
			}
		});
	}, []);

	const selectAllClick = () => {
		if (filteredTeamMembers.length === 0) return;

		if (selectedTeamMembers.length === 0) {
			setSelectedTeamMembers(filteredTeamMembers.map(tm => tm.EMPLOYEEID));
		} else {
			const allFilteredIds = filteredTeamMembers.map(tm => tm.EMPLOYEEID);
			const isAllFilteredIdsInSelected = allFilteredIds.every(id => selectedTeamMembers.includes(id));

			if (isAllFilteredIdsInSelected) {
				const removedSelectedIds = selectedTeamMembers.filter(id => !allFilteredIds.includes(id));
				setSelectedTeamMembers(removedSelectedIds);
			} else {
				const addedNewSelectedIds = selectedTeamMembers.concat(allFilteredIds);
				setSelectedTeamMembers(addedNewSelectedIds);
			}
		}
	};

	const teamMemberClick = (inputId: string) => {
		if (selectedTeamMembers.includes(inputId)) {
			const selectedUsers = selectedTeamMembers.filter(id => id !== inputId);
			setSelectedTeamMembers(selectedUsers);
		} else {
			const selectedUsers = [...selectedTeamMembers];
			selectedUsers.push(inputId);
			setSelectedTeamMembers(selectedUsers);
		}
	};

	const onImportClick = () => {
		setImporting(true);
		showLoader();
		Util.CommitMutation(
			ImportSageIntacctTeamMembersMutation,
			{
				sageIntacctEmployeesIds: selectedTeamMembers,
				permissionProfile: selectedProfileId,
				assignDepartment: assignDepartment,
				companyId: company.id,
			},
			() => {
				setImporting(false);
				closeModal();
				hideLoader();
				createToast({
					duration: 5000,
					message: `${selectedTeamMembers.length} Team Members imported from Sage Intacct`,
				});
			}
		);
	};

	const onFilterChange = (input: string) => {
		setFilteredTeamMembers(teamMembers.filter(tm => tm.NAME?.toLowerCase().includes(input.toLowerCase())));
	};

	return (
		<CreativeModal
			title={'Import Team Members from Sage Intacct'}
			size={'l'}
			closeModal={closeModal}
			creativeButtonText={`Import Selected Team Members (${selectedTeamMembers.length})`}
			cancelButtonText={formatMessage({id: 'common.cancel'})}
			onCreateHandler={onImportClick}
			onCancelHandler={closeModal}
			disabled={disableImportButton}
			replicateDesignSystem
		>
			<ComboBox
				label="Permissions"
				helperText={formatMessage({id: 'common.permissions_level'})}
				data-cy="combobox-profiles"
				defaultItems={profiles}
				value={selectedProfileId}
				onChange={setSelectedProfileId}
				width={ComboBox.WIDTH.FLEX}
			>
				{(profile: ForecastProfile) => (
					<ComboBox.Item textValue={profile.name || ''}>
						<Text>{profile.name}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>
			<FlexColumn gap={'xs'}>
				<Label>Team Members</Label>
				<Text color={Text.color.medium}>Please select the team members you wish to import from Sage Intacct</Text>
				<SearchField isExpandable={false} onChange={onFilterChange} width="600px" />
			</FlexColumn>
			{failed && <Text color={'error'}>Something has gone wrong, try again or contact support.</Text>}
			<ListItem>
				<FlexRow gap={'xl'}>
					<Checkbox onChange={selectAllClick} checked={selectAll} />
					<SubHeading size={'2'}>{'Select all'}</SubHeading>
				</FlexRow>
			</ListItem>
			<ListWrapper>
				{fetchingEmployees ? (
					<InlineLoader />
				) : (
					<Scrollbar maxHeight={scrollSectionHeight}>
						<FlexColumn alignItems="flex-start" justifyContent="flex-start">
							{filteredTeamMembers &&
								filteredTeamMembers.map(tm => {
									return (
										<ListItem key={'team-member-' + tm.EMPLOYEEID} hasBorderBottom>
											<FlexRow gap={'xl'}>
												<Checkbox
													onChange={() => teamMemberClick(tm.EMPLOYEEID)}
													checked={selectedTeamMembers.includes(tm.EMPLOYEEID)}
												/>
												<Text size={'2'}>{tm.NAME}</Text>
											</FlexRow>
										</ListItem>
									);
								})}
						</FlexColumn>
					</Scrollbar>
				)}
			</ListWrapper>
			<HorizontalBar />
			<Checkbox
				onChange={setAssignDepartment}
				checked={assignDepartment}
				description={'Assign imported team members to their Sage Intacct departments'}
			/>
		</CreativeModal>
	);
};

const sageIntacctImportTeamMembersModalQuery = graphql`
	query SageIntacctImportTeamMembersModal_Query {
		viewer {
			actualPersonId
			component(name: "sage_intacct_import_users_modal")
			company {
				...SageIntacctImportTeamMembersModal_company
			}
		}
	}
`;

export {sageIntacctImportTeamMembersModalQuery};

export default createFragmentContainer(SageIntacctImportTeamMembersModal, {
	company: graphql`
		fragment SageIntacctImportTeamMembersModal_company on Company {
			id
			profiles {
				edges {
					node {
						id
						name
						canBeEdited
						permissions
						disabled
					}
				}
			}
		}
	`,
});
